import { useState, useEffect, useRef, useCallback } from 'react';

const IDLE_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart'] as const;

export function useIdleLogout(
  timeoutMs: number,
  warningMs: number,
  onLogout: () => void,
) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAll = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const startTimers = useCallback(() => {
    clearAll();
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => {
      const warningDuration = Math.round((timeoutMs - warningMs) / 1000);
      setSecondsLeft(warningDuration);
      setShowWarning(true);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            clearInterval(countdownRef.current!);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }, warningMs);

    logoutTimerRef.current = setTimeout(() => {
      setShowWarning(false);
      onLogout();
    }, timeoutMs);
  }, [timeoutMs, warningMs, onLogout, clearAll]);

  const extendSession = useCallback(() => {
    setShowWarning(false);
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    startTimers();
    const reset = () => startTimers();
    IDLE_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      clearAll();
      IDLE_EVENTS.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [startTimers, clearAll]);

  return { showWarning, secondsLeft, extendSession };
}
